// paddle-michigan.com tenant configuration.
//
// All paddle-michigan-specific values that aren't just branded
// CSS variables (those live in app/globals.css under the
// [data-tenant="paddle-michigan"] selector). This file is the
// single source of truth for the launch river allowlist, the
// brand strings the layout/pages read at render time, and the
// footer attribution copy.
//
// Future tenants (paddle-wisconsin, paddle-minnesota, ...) get
// their own analogous config files; the tenant detection in
// lib/tenant.ts dispatches.

/** The 10 Michigan rivers that ship on paddle-michigan.com at
 *  launch (per the 2026-05-18 audit + the P1 kickoff). River
 *  Raisin is intentionally excluded — its NRP-stub data is
 *  flagged needsVerification (see TODO.md → DATA HYGIENE).
 *
 *  Each id MUST exist in data/rivers.ts (the tenant test
 *  validates this against getRiver). If you add a river here
 *  whose id doesn't resolve, the test breaks loudly. */
export const PADDLE_MICHIGAN_RIVER_IDS = [
  'pine_mi',          // Pine River — Jake's home; demo-ready
  'pere_marquette',   // Pere Marquette — demo-ready
  'manistee',         // Big Manistee — demo-ready (10 APs, 126 stocking rows)
  'muskegon',         // Muskegon — solid (4 APs, 23 stocking, 4 hatches)
  'sturgeon_lp',      // Sturgeon LP — empty AP map post-mig-069 ("empty > wrong")
  'rifle',            // Rifle — partial (3 APs, 6 stocking)
  'jordan',           // Jordan — partial (2 APs, 6 hatches)
  'white_mi',         // White — partial (3 APs, 4 stocking)
  'thunder_bay',      // Thunder Bay — thin (1 AP, 78 stocking)
  'flat_river',       // Flat — thinnest (1 AP, no supporting data)
] as const

export type PaddleMichiganRiverId = (typeof PADDLE_MICHIGAN_RIVER_IDS)[number]

/** Membership check used by tenant-aware routes to gate access:
 *  paddle-michigan.com/rivers/.../<slug> for a river not in this
 *  allowlist (e.g. River Raisin) → notFound(). */
export function isPaddleMichiganRiver(riverId: string): boolean {
  return (PADDLE_MICHIGAN_RIVER_IDS as readonly string[]).includes(riverId)
}

/** Brand content the layout swaps in when tenant=paddle-michigan.
 *  Style tokens (colors, etc.) live in globals.css as CSS
 *  variables; this is the non-CSS content (text + asset URLs). */
export const PADDLE_MICHIGAN_BRAND = {
  siteName: 'paddle-michigan',
  tagline: 'Michigan rivers, one map. Operated in partnership with MAPP.',
  // v1 wordmark: a text logo set in Playfair Display. v2 will swap
  // in a real graphic asset from Scott / a brand pass (TODO.md).
  wordmark: 'paddle-michigan',
  // Robots noindex for v1 (per Q9 ruling — paddle-michigan duplicates
  // river content from riverscout.app so we don't want both indexed
  // until paddle-michigan accumulates its own unique content). Flip
  // in lib/paddle-michigan-config.ts when ready.
  robotsIndexable: false,
  // Map river-label text color for the paddle-michigan StateRiverMap
  // (passed as the `labelColor` prop). Mapbox paint expressions are
  // JS values and cannot read CSS variables, so this MUST be kept in
  // sync by hand with the `--rv` value in the
  // `[data-tenant="paddle-michigan"]` block of app/globals.css
  // (#1B5E7A — "Great-Lakes blue"). RS's StateRiverMap default is
  // #085041; this is the paddle-michigan-branded equivalent so the
  // map labels don't bleed RS teal onto a paddle-michigan page.
  mapLabelColor: '#1B5E7A',
  footer: {
    // Per Q8 ruling. RiverScout link is rendered as an <a> in the
    // footer component; the © year is hard-coded for v1 (cheap to
    // update annually; ISR-friendly).
    line1:
      'paddle-michigan.com is operated in partnership with the Michigan ' +
      'Association of Paddlesport Providers (MAPP).',
    line2Prefix: 'Built on the ',
    line2LinkText: 'RiverScout',
    line2LinkUrl: 'https://riverscout.app',
    line2Suffix: ' platform. © 2026.',
  },
} as const

// ──────────────────────────────────────────────────────────────────
// PADDLE_MICHIGAN_SERVICES — controlled vocab for the MAPP-member
// directory's services-offered list (see migration 071,
// mapp_member_services). 13 keys: the 12 originally kicked off + the
// post-scrape addition `group_events` (multiple members offered
// "group of 55" outings, work parties, etc.). `food_service` was
// considered and explicitly excluded — amenity-adjacent, not a
// paddlesport service; belongs in long_description prose.
//
// ⚠ This is the single source of truth for the vocab. The DB does
// NOT carry a CHECK constraint on `service_key` — duplicating the
// list in SQL would create a Python↔TS-style cross-language sync
// hazard (same class as PR-b's TIER_CUTOFF_DAYS). The admin API
// validates submitted keys against `isPaddleMichiganServiceKey()`
// and returns a 400 with the full valid-key list on a typo.
//
// When SHRINKING the vocab: run a one-off cleanup query first
// (`delete from public.mapp_member_services where service_key not
// in (...)`), otherwise the DB will silently retain orphan rows
// the admin UI can no longer surface. See TODO.md.
// ──────────────────────────────────────────────────────────────────

export const PADDLE_MICHIGAN_SERVICES = [
  { key: 'kayak_rental',    label: 'Kayak rental' },
  { key: 'canoe_rental',    label: 'Canoe rental' },
  { key: 'raft_rental',     label: 'Raft rental' },
  { key: 'sup_rental',      label: 'SUP rental' },
  { key: 'tube_rental',     label: 'Tube rental' },
  { key: 'guided_trip',     label: 'Guided trips' },
  { key: 'shuttle_service', label: 'Shuttle service' },
  { key: 'lessons',         label: 'Lessons / instruction' },
  { key: 'gear_sales',      label: 'Gear sales' },
  { key: 'repair_service',  label: 'Repair service' },
  { key: 'lodging',         label: 'Lodging' },
  { key: 'camping',         label: 'Camping' },
  { key: 'group_events',    label: 'Group events / private trips' },
] as const

export type PaddleMichiganServiceKey = (typeof PADDLE_MICHIGAN_SERVICES)[number]['key']

export const PADDLE_MICHIGAN_SERVICE_KEYS: readonly string[] =
  PADDLE_MICHIGAN_SERVICES.map(s => s.key)

export function isPaddleMichiganServiceKey(k: string): boolean {
  return PADDLE_MICHIGAN_SERVICE_KEYS.includes(k)
}
